// ── Floor-Y lookup ─────────────────────────────────────────────────────────
// Returns the highest world-collision surface Y at the queried XZ that sits
// at or below `fromY`. Used by shell-casing physics, grenade trajectory/
// projectile bounces, and ragdoll bone resting positions so they snap to
// the correct floor on multi-floor maps (e.g., de_outpost_v6 drainage
// channel, catwalks, multi-story buildings).
//
// Audit findings: R-M-001 (shell casings), G-H-001 (grenade projectile),
// G-M-002 (ragdoll), 13 May audit Phase 3.
//
// Falls back to `fallback` (default 0) when no BVH is attached (e.g., a
// JSON-map path or pre-load frames) or when the downward ray misses
// everything — preserving the legacy `groundY = 0` behavior that those
// systems used before this helper existed.

import * as THREE from 'three';
import { getActiveCollisionBVH } from './collision';

// ── Reusable module-level temporaries (single allocation, no GC pressure) ──

const _origin = new THREE.Vector3();
const _down = new THREE.Vector3(0, -1, 0);
const _ray = new THREE.Ray(_origin, _down);

// ── Per-frame cache ────────────────────────────────────────────────────────
// Hot path: grenade trajectory preview runs the projectile sim ~60 steps
// per frame and shell-casing physics runs per active casing per frame, so
// dozens of floor lookups per frame land in roughly the same XZ buckets.
// The cache keys on a 25 cm XZ grid (Math.floor(coord * 4)) plus a 1 m
// fromY grid so stacked floors (drainage channel under bridge, catwalk
// over warehouse) don't alias to the same cache entry.

const _cache: Map<string, number> = new Map();

function cacheKey(x: number, z: number, fromY: number): string {
  const xb = Math.floor(x * 4);
  const zb = Math.floor(z * 4);
  const yb = Math.floor(fromY);
  return `${xb}|${zb}|${yb}`;
}

// ── Public API ─────────────────────────────────────────────────────────────

export interface FloorLookupOpts {
  /** Ray origin Y. Default 50. Pass current entity Y + a small epsilon to
   * see surfaces below the entity only (not catwalk above). */
  fromY?: number;
  /** Returned when no BVH or no hit. Default 0 (preserves legacy
   * groundY=0 behavior on JSON-map / pre-load). */
  fallback?: number;
}

/** Default ray origin Y. 50 m is comfortably above every shippable map
 *  (de_outpost_v6 tops out around 30 m at the watchtower roofs). */
const DEFAULT_FROM_Y = 50;

/**
 * Uncached floor-Y query. Casts a downward ray against the world BVH and
 * returns the first hit's Y. Use this in cold paths or when callers want
 * a guaranteed-fresh result; hot per-frame loops should prefer
 * `floorYAtCached` below.
 */
export function floorYAt(x: number, z: number, opts?: FloorLookupOpts): number {
  const fallback = opts?.fallback ?? 0;
  const fromY = opts?.fromY ?? DEFAULT_FROM_Y;

  const handle = getActiveCollisionBVH();
  if (!handle) return fallback;

  _origin.set(x, fromY, z);
  // _down is already (0, -1, 0); set every call defensively in case any
  // future code mutates it.
  _down.set(0, -1, 0);
  _ray.origin = _origin;
  _ray.direction = _down;

  const hit = handle.bvh.raycastFirst(_ray);
  if (!hit) return fallback;
  return hit.point.y;
}

/**
 * Per-frame XZ+fromY cached floor-Y query. Call `beginFloorLookupFrame()`
 * once at the top of each render frame to clear the cache. Cache buckets
 * are 25 cm in XZ and 1 m in fromY.
 */
export function floorYAtCached(x: number, z: number, opts?: FloorLookupOpts): number {
  const fromY = opts?.fromY ?? DEFAULT_FROM_Y;
  const key = cacheKey(x, z, fromY);
  const cached = _cache.get(key);
  if (cached !== undefined) return cached;
  const y = floorYAt(x, z, opts);
  _cache.set(key, y);
  return y;
}

/** Clear the per-frame floor-lookup cache. Call once at frame start. */
export function beginFloorLookupFrame(): void {
  _cache.clear();
}
